import React from "react";
import {
  Tree,
  putCommasIntoNumber,
  User,
  LocalProject,
  TileVariant,
} from "@ecologi/shared";
import styled from "@emotion/styled";
import { css } from "@emotion/react";
import {
  ForestTile,
  ForestTileProps,
  ForestTileInner,
  ForestTileSparkles,
  ForestTileStack,
  ForestTileStackPost,
  ForestTilePopover,
} from "../ForestTile";
import { getBirthdayTreeSVGs, getTileKeyName, treeFlagMapper } from "./utils";
import Image from "next/legacy/image";
import { ImpactFundTileBadge } from "../ImpactFundTileBadge";

type ForestTileBaseProps = ForestTileProps & {
  isBirthday?: boolean;
  isLocalProject?: boolean;
  treeYear: string;
  tileKeyName: string;
};

const ForestTileBase = styled(ForestTile)<ForestTileBaseProps>`
  cursor: pointer;

  // for svg graphic overlays of actual trees
  &::after {
    background-image: ${(props) => props.backgroundImage};
    background-position: 50%;
    background-repeat: no-repeat;
    background-size: 100%;
    top: 0;
    bottom: 0;
    right: 0;
    left: 0;
    content: "";
    display: block;
    position: absolute;
    transform: scaleX(${(props) => (props.isFlipped ? "-1" : "1")});
  }

  ${(props) =>
    props.isBirthday &&
    getBirthdayTreeSVGs(props.treeYear)[props.tileKeyName] &&
    css`
      &::after {
        background-image: url(${getBirthdayTreeSVGs(props.treeYear)[
          props.tileKeyName
        ]});
      }
    `}

  ${(props) =>
    props.isBirthday &&
    props.treeYear === "2021" &&
    props.tileKeyName === "month5SingleTree" &&
    css`
      &::after {
        background-size: 90%;
      }
    `}

  ${(props) =>
    props.isLocalProject &&
    css`
      &::after {
        background-position-y: 45%;
      }
    `}
`;

const CountryFlag = styled.div<{
  country: LocalProject["country"];
  isShiftedHigher?: boolean;
}>`
  display: block;
  position: absolute;
  transform: translateX(50%);
  right: 51%;
  bottom: 12px;
  width: 22px;
  height: 22px;

  @media ${({ theme }) => theme.bp.tabletUp} {
    bottom: ${(props) => (props.isShiftedHigher ? "42px" : "22px")};
    height: 34px;
    width: 34px;
  }
`;

export type ForestTileTreeProps = {
  isLinkedTile: boolean;
  isNew?: boolean;
  isNudged: boolean;
  numbering: number;
  onClick: () => void;
  profile: User;
  tree: Tree;
};

/**
 * Do not extend! Please instead refactor into `<ForestTileImpact>`
 */
export const ForestTileTree = ({
  isLinkedTile,
  isNew,
  isNudged,
  numbering,
  onClick,
  profile,
  tree,
}: ForestTileTreeProps) => {
  const tileKeyName = getTileKeyName(tree);
  const backgroundImage = `url(/images/app/forest/trees/${tileKeyName}.svg)`;
  const title = `Learn more about ${
    tree.value === 1 ? "this tree" : "these trees"
  }`;

  const treeQuantity = putCommasIntoNumber(tree.value);
  const isLargeTreeGroup = tree.value > 3;

  const showStack = isLargeTreeGroup && !tree.isDividend && !tree.callout;
  const showStackSignpost =
    tree.value >= 250 && tree.isDonation && !profile.hasNameAsCallout;
  const showCallout =
    !tree.localProject &&
    (tree.callout || (profile.hasNameAsCallout && tree.name));

  return (
    <ForestTileBase
      id={isNew ? "your-tile" : isLinkedTile ? tree._id : ""}
      onClick={onClick}
      tileKeyName={tileKeyName}
      backgroundImage={backgroundImage}
      treeYear={tree.treeYear}
      isFlipped={tree.style === "b" && !tree.isBirthday}
      isNudged={isNudged}
      isBirthday={tree.isBirthday}
      isLocalProject={!!tree.localProject}
      title={title}
      $isCentered={numbering === 1}
      data-testid="tile"
    >
      <ForestTileInner>
        {tree.variant === TileVariant.IMPACT_FUND && (
          <ImpactFundTileBadge data-testid="tile-impact-fund-badge" />
        )}

        {tree.isDividend && <ForestTileStackPost value="Tree dividend" />}

        {tree.localProject && (
          <CountryFlag
            country={tree.localProject.country}
            isShiftedHigher={isLargeTreeGroup}
            data-testid="forest-tile-local-flag"
          >
            <Image
              layout="fill"
              src={treeFlagMapper[tree.localProject.country]}
            />
          </CountryFlag>
        )}

        {showStack && showStackSignpost && (
          <ForestTileStackPost value={treeQuantity} />
        )}

        {showStack && !showStackSignpost && (
          <ForestTileStack value={treeQuantity} />
        )}

        {showCallout && (
          <ForestTileStackPost
            value={tree.callout ? tree.callout : tree.name}
            isCentered={isLargeTreeGroup}
            isCallout={profile.hasNameAsCallout}
          />
        )}

        {tree.variant === TileVariant.REFERRAL && (
          <ForestTileSparkles isSingle={tree.value === 1} />
        )}

        {(isNew || isLinkedTile) && (
          <ForestTilePopover
            message={`Your tree${tree.value > 1 ? "s" : ""}`}
            data-testid="forest-tile-popover"
          />
        )}
      </ForestTileInner>
    </ForestTileBase>
  );
};
